import { ModelDropdown } from "@/components/molecules/ModelDropdown/ModelDropdown";
import { FormField, type FormFieldProps } from "@/overlay/components/form-field";
import { searchEnginesWeatherParams, useSearchEngineReady, useWeatherParamModels } from "@/weather-parameters";
import { t } from "@lingui/core/macro";
import type { ModelSchema } from "@mm/api-layers.meteomatics.com";
import {
  type ExactMatchResult,
  type HasLayerIdxNarrowing,
  type PartialWeatherParameter,
  autofill,
  parameterToString,
} from "@mm/weather-parameter-utils";
import React, { useEffect, useState } from "react";

export function ModelDropdownHavingParam(props: {
  parameterStr: string;
  onChange: (model: string) => void;
  selectedModel?: string;
  formFieldProps: Partial<FormFieldProps>;
  allowedModels?: string[];
}) {
  const weatherModels = useWeatherParamModels();
  const [models, setModels] = useState<ModelSchema[] | null>(null);
  const [localParameter, setLocalParameter] = useState<HasLayerIdxNarrowing<PartialWeatherParameter> | null>(null);

  const searchEngineReady = useSearchEngineReady();

  useEffect(() => {
    let isSubscribed = true;
    if (
      (!localParameter && searchEngineReady) ||
      (localParameter && parameterToString(localParameter).formatted !== props.parameterStr && searchEngineReady)
    ) {
      if (!searchEnginesWeatherParams.standard || !props.parameterStr) {
        return;
      }

      searchEnginesWeatherParams.standard
        .search(props.parameterStr)
        .then((response) => {
          if (isSubscribed) {
            if (response.results?.regular?.[0]) {
              const result: ExactMatchResult = response.results.regular[0];
              setLocalParameter(
                autofill({
                  parameter: result.layerGroup,
                  narrowed_selection: {
                    layerIdx: result.matchingLayers[0].layerIdx,
                    // biome-ignore lint/style/noNonNullAssertion: TODO this is becoming unacceptable
                    fields: result.matchingLayers[0].match.groups!,
                  },
                }),
              );
            } else {
              console.warn(`Search for parameter ${localParameter} errored out.`);
            }
          }
        })
        .catch((e) => {
          console.warn(`Search for parameter close to ${localParameter} errored out.`);
        });
    }
    return () => {
      isSubscribed = false;
    };
  }, [props.parameterStr, localParameter, searchEngineReady]);

  useEffect(() => {
    if (weatherModels) {
      setModels(
        weatherModels.filter((model) => {
          const isAvailable = localParameter
            ? localParameter.parameter.available_models?.includes(model.identifier)
            : false;
          const isAllowed = props.allowedModels ? props.allowedModels.includes(model.identifier) : true;
          return isAvailable && isAllowed;
        }),
      );
    }
  }, [weatherModels, localParameter, props.allowedModels]);

  return (
    <FormField label={t`Source`} {...props.formFieldProps}>
      <ModelDropdown
        options={models?.map((model) => ({ id: model.identifier, title: `${model.name}` })) || []}
        value={props.selectedModel}
        onChange={props.onChange}
        placeholder={"Select a model"}
      />
    </FormField>
  );
}
